// src/components/sessions/SessionItem.jsx
import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  VideoCameraIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import AttendanceList from './AttendanceList';
import SessionReportPanel from './SessionReportPanel';
import RecordingsDropdown from './RecordingsDropdown';
import { getSessionRecordings } from '@/services/api';

// Helper functions
function formatDate(dateString, locale = 'en-US') {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function formatTime(dateStr, locale = 'en-US') {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

function formatDuration(seconds, t) {
  if (!seconds || seconds < 0) return t('common.notAvailable');
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return t('time.duration.hoursMinutesSeconds', { hours, minutes, seconds: secs });
  } else if (minutes > 0) {
    return t('time.duration.minutesSeconds', { minutes, seconds: secs });
  } else {
    return t('time.duration.seconds', { seconds: secs });
  }
}

function LiveSessionTimer({ secondsSinceStart, actualStartTime }) {
  const { t } = useTranslation();
  
  const calculateTimeAgo = useCallback(() => {
    let elapsed;
    
    if (secondsSinceStart !== null && secondsSinceStart !== undefined) {
      const initialSeconds = secondsSinceStart;
      const startTime = Date.now() - (initialSeconds * 1000);
      elapsed = Math.floor((Date.now() - startTime) / 1000);
    } else if (actualStartTime) {
      elapsed = Math.floor((Date.now() - new Date(actualStartTime).getTime()) / 1000);
    } else {
      return '';
    }
    
    const minutes = Math.floor(elapsed / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return t('time.started.hoursMinutesAgo', { hours, minutes: minutes % 60 });
    } else if (minutes > 0) {
      return t('time.started.minutesAgo', { minutes });
    } else {
      return t('time.started.justStarted');
    }
  }, [secondsSinceStart, actualStartTime, t]);

  return <span className="text-xs text-green-600">{calculateTimeAgo()}</span>;
}

function CountdownTimer({ startTime }) {
  const { t } = useTranslation();
  
  const calculateTimeUntil = useCallback(() => {
    const now = new Date();
    const sessionTime = new Date(startTime);
    const diff = sessionTime.getTime() - now.getTime();

    if (diff <= 0) {
      return t('time.startingSoon');
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 1) {
      return t('time.countdown.days', { count: days });
    } else if (days === 1) {
      return t('time.countdown.oneDayHours', { hours });
    } else if (hours > 0) {
      return t('time.countdown.hoursMinutes', { hours, minutes });
    } else {
      return t('time.countdown.minutes', { count: minutes });
    }
  }, [startTime, t]);

  return <span>{calculateTimeUntil()}</span>;
}

function DynamicTimeAgo({ time }) {
  const { t } = useTranslation();
  
  const calculateTimeAgo = useCallback(() => {
    if (!time) return '';
    
    const diff = new Date().getTime() - new Date(time).getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 1) {
      return t('time.ago.days', { count: days });
    } else if (days === 1) {
      return t('time.ago.oneDay');
    } else if (hours > 0) {
      return t('time.ago.hoursMinutes', { hours, minutes: minutes % 60 });
    } else if (minutes > 0) {
      return t('time.ago.minutes', { count: minutes }); 
    } else {
      return t('time.ago.justNow');
    }
  }, [time, t]);

  return <span>{calculateTimeAgo()}</span>;
}

const SessionItem = memo(({ 
  session, 
  isUpcoming, 
  userRole, 
  onJoinSession, 
  onDeleteSession, 
  onViewChat,
  isOwner = false,
  sessionStatus,
  sessionData
}) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState(0);
  const updateTimerRef = useRef(null);
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const hasFetchedRecordings = useRef(false);
  
  const mergedSession = React.useMemo(() => {
    if (sessionData && sessionStatus === 'ended') {
      return { ...session, ...sessionData };
    }
    return session;
  }, [session, sessionData, sessionStatus]);
  
  const now = new Date();
  const startTime = new Date(mergedSession.start_time || mergedSession.startTime);
  const endTime = new Date(mergedSession.end_time || mergedSession.endTime);
  
  const currentStatus = sessionStatus || mergedSession.session_status || mergedSession.status;
  
  const isLive = currentStatus === 'live' || 
                 mergedSession.started === true ||
                 mergedSession.is_started === true;
  
  const hasRecording = mergedSession.recording_enabled || 
                      mergedSession.recordingEnabled;
  
  const isPast = currentStatus === 'ended' || 
                 currentStatus === 'cancelled' ||
                 (!isLive && (!isUpcoming || mergedSession.ended || now > endTime));
  
  const isTeacher = userRole === 'teacher';
  
  // Fixed: Fetch recordings only once
  useEffect(() => {
    const fetchRecordings = async () => {
      // Only fetch once
      if (hasFetchedRecordings.current) return;
      if (!isPast || !hasRecording || !mergedSession.id) return;
      
      hasFetchedRecordings.current = true;
      setLoadingRecordings(true);
      
      try {
        const response = await getSessionRecordings(mergedSession.id);
        if (response.success && response.data) {
          setRecordings(response.data);
        } else {
          // Set empty array to mark as "fetched" even if no recordings
          setRecordings([]);
        }
      } catch (err) {
        console.error('Failed to load recordings:', err);
        // Set empty array to mark as "fetched" even on error
        setRecordings([]);
      } finally {
        setLoadingRecordings(false);
      }
    };

    fetchRecordings();
  }, [isPast, hasRecording, mergedSession.id]);
  
  const scheduleNextUpdate = useCallback(() => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    if (isPast || isLive) return;

    let nextUpdateTime = null;
    const now = new Date();

    if (!isLive && now < startTime) {
      const fiveMinsBefore = new Date(startTime.getTime() - 5 * 60 * 1000);
      if (fiveMinsBefore > now) {
        nextUpdateTime = fiveMinsBefore;
      }
    }

    if (!isLive && startTime > now && (!nextUpdateTime || startTime < nextUpdateTime)) {
      nextUpdateTime = startTime;
    }

    if (!isLive && endTime > now && (!nextUpdateTime || endTime < nextUpdateTime)) {
      nextUpdateTime = endTime;
    }

    if (nextUpdateTime) {
      const delay = Math.max(0, nextUpdateTime.getTime() - now.getTime());
      
      updateTimerRef.current = setTimeout(() => {
        forceUpdate(prev => prev + 1);
        scheduleNextUpdate();
      }, delay);
    }
  }, [isPast, isLive, startTime, endTime]);

  useEffect(() => {
    scheduleNextUpdate();
    
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, [scheduleNextUpdate, currentStatus]);
  
  const teacherCanJoin = (() => {
    const currentNow = new Date();
    if (!isTeacher) return false;
    if (isLive) return true;
    if (isPast || mergedSession.ended || currentNow > endTime) return false;
    
    const fiveMinutesBefore = new Date(startTime.getTime() - 5 * 60 * 1000);
    return currentNow >= fiveMinutesBefore && currentNow <= endTime;
  })();
  
  const studentWaitingForTeacher = (() => {
    const currentNow = new Date();
    if (isTeacher || isLive || isPast) return false;
    const fiveMinutesBefore = new Date(startTime.getTime() - 5 * 60 * 1000);
    return currentNow >= fiveMinutesBefore && currentNow <= endTime;
  })();
  
  const canJoin = isLive || (isTeacher && teacherCanJoin);
  
  const actualStartTime = mergedSession.actual_start_time || 
                          mergedSession.actualStartTime || 
                          mergedSession.actualStart ||
                          (isLive && !mergedSession.actual_start_time ? new Date().toISOString() : null);
                          
  const actualEndTime = mergedSession.actual_end_time || 
                       mergedSession.actualEndTime || 
                       mergedSession.actualEnd;
                       
  const totalConnectedTime = mergedSession.total_connected_time || 
                            mergedSession.totalConnectedTime || 
                            mergedSession.connected_time;
  
  const attendedCount = mergedSession.attended_count ?? 
                       mergedSession.attendedCount ?? 0;
                       
  const totalEnrolledStudents = mergedSession.total_enrolled_students || 
                                mergedSession.totalEnrolledStudents || 
                                mergedSession.total_students || 0;
  
  const hasChatLogs = mergedSession.chat_log_available || 
                     mergedSession.chatLogAvailable ||
                     mergedSession.has_chat_logs;

  const attendanceDetails = mergedSession.attendance_details || 
                           mergedSession.attendanceDetails ||
                           mergedSession.attendance;

  const studentAttendanceRecord = React.useMemo(() => {
    if (userRole !== 'student' || !attendanceDetails) return null;
    
    return attendanceDetails.find(record => 
      record.user_id === session.current_user_id || 
      record.attended !== undefined
    );
  }, [userRole, attendanceDetails, session.current_user_id]);

  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all border ${
      isLive ? 'border-green-400' : 'border-slate-200'
    }`}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap flex-1">
            <h3 className="text-lg font-semibold text-slate-800">
              {mergedSession.title || t('sessions.noTitle')}
            </h3>
            {mergedSession.session_number && (
              <span className="text-xs px-2 py-0.5 bg-cyan-50 rounded-full text-cyan-600">
                {t('sessions.sessionNumber', { number: mergedSession.session_number })}
              </span>
            )}
            {isLive && (
              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                {t('sessions.status.liveNow')}
              </span>
            )}
            {hasRecording && !isPast && (
              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium flex items-center inline-flex">
                <div className="w-1.5 h-1.5 bg-red-600 rounded-full mr-1 animate-pulse" />
                {t(isLive ? 'sessions.recording.on' : 'sessions.recording.enabled')}
              </span>
            )}
            {hasRecording && isPast && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                {t('sessions.recording.wasRecorded')}
              </span>
            )}
          </div>
          
          {isUpcoming && isTeacher && !isLive && (
            <button
              onClick={() => onDeleteSession(mergedSession.id)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-4"
              title={t('sessions.buttons.delete')}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {hasRecording && !isPast && (
          <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <VideoCameraIcon className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-700">
              <span className="font-medium">
                {t(isLive ? 'sessions.recording.beingRecorded' : 'sessions.recording.willBeRecorded')}
              </span>
              <p className="mt-1 text-red-600">
                {t('sessions.recording.captureNotice')}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center gap-6 text-sm">
            <span className="flex items-center text-slate-700">
              <CalendarIcon className="w-4 h-4 mr-1.5" />
              {formatDate(mergedSession.start_time || mergedSession.startTime, i18n.language)}
            </span>
            <span className="flex items-center text-slate-600">
              <ClockIcon className="w-4 h-4 mr-1.5" />
              {formatTime(mergedSession.start_time || mergedSession.startTime, i18n.language)} - {formatTime(mergedSession.end_time || mergedSession.endTime, i18n.language)}
              {isUpcoming && !isLive && !studentWaitingForTeacher && now < startTime && (
                <span className="ml-2 text-blue-600 font-medium">
                  ({t('time.startsIn')} <CountdownTimer startTime={mergedSession.start_time || mergedSession.startTime} />)
                </span>
              )}
            </span>
          </div>
          
          {isLive && actualStartTime && (
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center text-green-600">
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                <LiveSessionTimer 
                  secondsSinceStart={mergedSession.secondsSinceStart || mergedSession.seconds_since_start}
                  actualStartTime={actualStartTime}
                />
              </span>
              <span className="text-xs text-slate-500">
                {t('sessions.startedAt', { time: formatTime(actualStartTime, i18n.language) })}
              </span>
            </div>
          )}
          
          {isPast && actualStartTime && actualEndTime && (
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center text-green-600">
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                {t('sessions.actualTime', { 
                  start: formatTime(actualStartTime, i18n.language), 
                  end: formatTime(actualEndTime, i18n.language) 
                })}
              </span>
              <span className="text-slate-500">
                (<DynamicTimeAgo time={actualEndTime} />)
              </span>
            </div>
          )}
          
          {isPast && !actualStartTime && !actualEndTime && (
            <div className="text-xs text-slate-500 italic">
              {t('sessions.endedNoTimes')}
            </div>
          )}
          
          {studentWaitingForTeacher && (
            <div className="text-sm text-orange-600 font-medium">
              {t('sessions.waitingForTeacher')}
            </div>
          )}
          
          <div className="flex items-center text-sm text-slate-600">
            <UserIcon className="w-4 h-4 mr-1.5" />
            {mergedSession.tutor_name || mergedSession.tutorName || mergedSession.teacher_name || t('sessions.noTutorAssigned')}
          </div>
        </div>

        {isPast && !isLive && attendanceDetails && isTeacher && (
          <AttendanceList 
            attendanceDetails={attendanceDetails} 
            sessionId={mergedSession.id}
          />
        )}
        
        {isPast && !isLive && (!attendanceDetails || userRole === 'student') && (
          <div className="flex items-center gap-3 flex-wrap">
            {userRole === 'student' && (
              <>
                <span className={`text-xs px-2 py-1 rounded-full flex items-center ${
                  (studentAttendanceRecord?.attended ?? mergedSession.attended)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {(studentAttendanceRecord?.attended ?? mergedSession.attended) ? (
                    <>
                      <CheckCircleIcon className="w-3 h-3 mr-1" />
                      {t('sessions.attendance.attended')}
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="w-3 h-3 mr-1" />
                      {t('sessions.attendance.absent')}
                    </>
                  )}
                </span>
                
                {(studentAttendanceRecord?.attended ?? mergedSession.attended) && 
                 (studentAttendanceRecord?.total_connected_time || totalConnectedTime) && (
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {t('sessions.attendance.connected')}: {formatDuration(studentAttendanceRecord?.total_connected_time || totalConnectedTime, t)}
                  </span>
                )}
              </>
            )}
            
            {isTeacher && !attendanceDetails && (
              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                {t('sessions.attendance.participantCount', { 
                  attended: attendedCount, 
                  total: totalEnrolledStudents 
                })}
              </span>
            )}
          </div>
        )}

        {/* Session Reports */}
        {isPast && !isLive && (
          <SessionReportPanel
            sessionId={mergedSession.id}
            userRole={userRole}
            attendanceDetails={attendanceDetails}
          />
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(isUpcoming || isLive || studentWaitingForTeacher) && (
              <button
                onClick={canJoin ? () => onJoinSession(mergedSession) : undefined}
                disabled={!canJoin}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center font-medium ${
                  isLive
                    ? 'bg-green-600 text-white hover:bg-green-700 cursor-pointer'
                    : canJoin
                      ? 'bg-cyan-600 text-white hover:bg-cyan-700 cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={!canJoin && !isTeacher ? t('sessions.tooltips.notStarted') : 
                       !canJoin && isTeacher ? t('sessions.tooltips.startWindow') : ''}
              >
                <VideoCameraIcon className="w-5 h-5 mr-2" />
                {isLive ? t('sessions.buttons.joinLive') : t('sessions.buttons.join')}
              </button>
            )}
            
            {isPast && !isLive && (
              <>
                {(isOwner || isTeacher) && hasRecording && (
                  loadingRecordings ? (
                    <button
                      disabled
                      className="px-3 py-1.5 bg-gray-300 text-gray-500 text-sm rounded-lg flex items-center cursor-wait"
                    >
                      <VideoCameraIcon className="w-4 h-4 mr-1.5" />
                      {t('common.loading')}
                    </button>
                  ) : recordings.length > 0 ? (
                    // Check if all recordings failed
                    recordings.every(r => r.status === 'failed') ? (
                      <button
                        disabled
                        className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg flex items-center cursor-not-allowed"
                        title={t('sessions.recording.failed')}
                      >
                        <VideoCameraIcon className="w-4 h-4 mr-1.5" />
                        {t('sessions.recording.failed')}
                      </button>
                    ) : recordings.some(r => r.status === 'available') ? (
                      <RecordingsDropdown 
                        recordings={recordings.filter(r => r.status === 'available')}
                        sessionInfo={{
                          title: mergedSession.title,
                          courseName: mergedSession.course_name || mergedSession.courseName,
                          sessionNumber: mergedSession.session_number || mergedSession.sessionNumber
                        }}
                      />
                    ) : (
                      // Some recordings are processing
                      <button
                        disabled
                        className="px-3 py-1.5 bg-blue-100 text-blue-600 text-sm rounded-lg flex items-center cursor-wait"
                        title={t('sessions.recording.availableSoon')}
                      >
                        <VideoCameraIcon className="w-4 h-4 mr-1.5" />
                        {t('sessions.recording.availableSoon')}
                      </button>
                    )
                  ) : (
                    // recording_enabled=true but no DB entries
                    // Use time-based check: if >30 mins passed since session end, recording failed
                    (() => {
                      const sessionEndTime = new Date(actualEndTime || mergedSession.end_time || mergedSession.endTime);
                      const minutesSinceEnd = (Date.now() - sessionEndTime.getTime()) / (1000 * 60);
                      const RECORDING_TIMEOUT_MINUTES = 30;
                      
                      if (minutesSinceEnd > RECORDING_TIMEOUT_MINUTES) {
                        return (
                          <button
                            disabled
                            className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg flex items-center cursor-not-allowed"
                            title={t('sessions.recording.failed')}
                          >
                            <VideoCameraIcon className="w-4 h-4 mr-1.5" />
                            {t('sessions.recording.failed')}
                          </button>
                        );
                      } else {
                        return (
                          <button
                            disabled
                            className="px-3 py-1.5 bg-blue-100 text-blue-600 text-sm rounded-lg flex items-center cursor-wait"
                            title={t('sessions.recording.availableSoon')}
                          >
                            <VideoCameraIcon className="w-4 h-4 mr-1.5" />
                            {t('sessions.recording.availableSoon')}
                          </button>
                        );
                      }
                    })()
                  )
                )}
                
                {hasChatLogs ? (
                  <button 
                    onClick={() => onViewChat(mergedSession)}
                    className="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 flex items-center transition-colors"
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1.5" />
                    {t('sessions.buttons.viewChat')}
                  </button>
                ) : (
                  <button 
                    disabled
                    className="px-3 py-1.5 bg-gray-200 text-gray-400 text-sm rounded-lg flex items-center cursor-not-allowed"
                    title={t('sessions.tooltips.noChatMessages')}
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1.5" />
                    {t('sessions.buttons.viewChat')}
                  </button>
                )}
              </>
            )}
          </div>
          
          <div>
            {!isLive && !isPast && studentWaitingForTeacher && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-orange-100 text-orange-700">
                {t('sessions.status.waitingForTeacher')}
              </span>
            )}
            {!isLive && !isPast && teacherCanJoin && !studentWaitingForTeacher && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-yellow-100 text-yellow-700">
                {t('sessions.status.readyToStart')}
              </span>
            )}
            {!isLive && !isPast && !teacherCanJoin && !studentWaitingForTeacher && now < startTime && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-slate-100 text-slate-700">
                {t('sessions.status.upcoming')}
              </span>
            )}
            {isPast && !isLive && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-700">
                {t('sessions.status.completed')}
              </span>
            )}
            {isLive && (
              <span className="text-xs px-2 py-1 rounded-full font-medium bg-green-100 text-green-700 animate-pulse">
                {t('sessions.status.inProgress')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.session.id === nextProps.session.id &&
    prevProps.sessionStatus === nextProps.sessionStatus &&
    prevProps.sessionData === nextProps.sessionData &&
    prevProps.session.session_status === nextProps.session.session_status &&
    prevProps.session.started === nextProps.session.started &&
    prevProps.session.actual_start_time === nextProps.session.actual_start_time &&
    prevProps.session.actual_end_time === nextProps.session.actual_end_time &&
    prevProps.session.chat_log_available === nextProps.session.chat_log_available &&
    prevProps.session.attendance_details === nextProps.session.attendance_details &&
    prevProps.session.attendance === nextProps.session.attendance &&
    prevProps.isUpcoming === nextProps.isUpcoming &&
    prevProps.userRole === nextProps.userRole &&
    prevProps.isOwner === nextProps.isOwner
  );
});

SessionItem.displayName = 'SessionItem';

export default SessionItem;